/**
 * kult-cart.js
 * Gemeinsame Warenkorb-Logik für <kult-cart-drawer> (Drawer) und
 * <kult-cart-page> (Warenkorb-Seite). Zeilen über /cart/change.js, Kult-Abo-Gruppen (_kultabo_set) über
 * /cart/add.js + /cart/update.js (changeAboSet),
 * Cross-Sell über /cart/add.js, Gutscheine über /cart/update.js.
 * Willkommenscode (10.10.2026): data-welcome-codes am Root; ohne Kundenkonto oder ohne aktiven Newsletter
 * (data-customer-logged-in, data-newsletter-active) wird er nicht angewendet (kein Request), ein schon liegender Code
 * (aus /cart.js discount_codes, beim Laden nur bei gefülltem Warenkorb) wird entfernt, jeweils mit Hinweis und Link. Die Kasse prüft zusätzlich das Segment.
 * Firmendaten ([data-cart-b2b-form]) werden als Warenkorb-Attribute über
 * /cart/update.js gespeichert (Feldname = Attributname).
 * Das eigene HTML wird per Section-Rendering-API (?section_id=…) neu geladen
 * (data-section-id + data-section-url). Nach jeder Änderung wird
 * "kult:cart:updated" (Detail: cart, source) gesendet: Header-Badge,
 * PDP-Versandbalken und das jeweils andere Warenkorb-Element hören zu.
 * Drawer öffnet über "kult:cart:add" (PDP/Cross-Sell) oder [data-kult-cart-open].
 * Firmenkundenbereich (Header data-kult-mode="b2b"): kein Drawer, stattdessen Warenkorbseite; Privatkundenseiten behalten den Drawer.
 * Keine externen Libs, keine Storage-Zugriffe.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* Karton-Staffel der Sammellieferung (19.10.2026), identisch mit shipCartons in assets/kult-b2b-builder.js:
     je volle per Präsente ein großer Karton; Rest gierig in kleine Kartons (mid), ein einzelnes übriges Präsent als Einzelpaket.
     mid = 0 (keine 5er-Variante) → alte Regel: Rest 1 = Einzel, Rest 2+ = großer Karton. */
  function shipCartons(n, per, mid) {
    var u = { single: 0, mid: 0, bulk: Math.floor(n / per) }, rest = n % per;
    if (!(mid > 1)) { if (rest === 1) u.single = 1; else if (rest > 1) u.bulk += 1; return u; }
    // Rest günstigst packen: 1 → Einzel, 2–5 → 5er, 6 → 5er + Einzel (19,39 < 21,42), 7–9 → ein 10er (21,42 < 2 × 11,90)
    if (rest >= mid + 2) { u.bulk += 1; rest = 0; }
    while (rest > 0) { if (rest === 1) { u.single += 1; rest = 0; } else { u.mid += 1; rest -= Math.min(rest, mid); } }
    return u;
  }

  function formatPrice(cents) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
  }

  /* Willkommenscode-Wächter (gemeinsam für Drawer und Seite): nur ein Entfernen gleichzeitig; Hinweis überlebt das Neu-Rendern beider Elemente */
  var welcomeState = { busy: false, kind: '', at: 0 };

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function postJson(url, body) {
    return fetch(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  }

  /* Firmenbestellung-Reste (Cowork 12.10.2026): Das Warenkorb-Attribut „Firmenbestellung“ (Konfigurator, Firmenkunden-Shop,
     Bürokaffee) blieb nach „Warenkorb leeren“ oder nach dem Entfernen der letzten Firmenposition stehen; die Kasse rechnete
     dann auch eine private Bestellung mit den Firmen-Versandstufen. Nach jeder Warenkorb-Änderung (refresh) und beim Laden
     eines leeren Warenkorbs: Liegt keine Zeile mit Eigenschaft _kultb2b… im Warenkorb (Präsent-Gruppen: _kultb2b, Bürokaffee und
     Firmenkunden-Shop: _kultb2b_quelle), werden alle Firmen-Attribute per /cart/update.js geleert. Wird der Warenkorb
     durch eine Änderung leer, entfallen zusätzlich die lokalen Merker (localStorage kult:b2b, sessionStorage kult:bereich;
     Letzterer bleibt auf Firmenkunden-Seiten stehen, weil er den Bereich der Sitzung beschreibt). */
  var B2B_ATTRIBUTES = ['Firmenbestellung', 'Einzelversand', 'Firma', 'USt-IdNr.', 'Bestellreferenz', 'Lieferung Präsente'];
  var b2bCleanup = null;
  var b2bLoadChecked = false;

  /* Firmenzeile = Eigenschaft _kultb2b (Präsent-Gruppen) ODER _kultb2b_quelle (Bürokaffee, Firmenkunden-Shop); gleiches Kriterium wie assets/kult-b2b-reset.js */
  function hasB2bLine(cart) {
    return ((cart && cart.items) || []).some(function (it) {
      var p = it.properties || {};
      return Object.keys(p).some(function (k) { return k.indexOf('_kultb2b') === 0; });
    });
  }

  function inB2bArea() {
    var h = document.querySelector('[data-kult-mode]');
    return !!h && h.getAttribute('data-kult-mode') === 'b2b';
  }

  function dropB2bMarkers() {
    try { window.localStorage.removeItem('kult:b2b'); } catch (e) { /* kein Speicher */ }
    try { if (!inB2bArea()) window.sessionStorage.removeItem('kult:bereich'); } catch (e) { /* kein Speicher */ }
  }

  /* Liefert immer ein Promise mit dem (ggf. bereinigten) Warenkorb; Fehler lassen den Warenkorb unverändert. */
  function cleanB2bLeftovers(cart, fromLoad) {
    if (!cart || hasB2bLine(cart)) return Promise.resolve(cart);
    var empty = !(cart.items && cart.items.length);
    if (empty && !fromLoad) dropB2bMarkers(); // beim bloßen Laden bleiben die Merker (Bereich der Sitzung auf gemeinsamen Seiten)
    var attrs = cart.attributes || {};
    var stale = {};
    var any = false;
    B2B_ATTRIBUTES.forEach(function (k) { if (attrs[k]) { stale[k] = ''; any = true; } });
    if (!any) return Promise.resolve(cart);
    if (b2bCleanup) return b2bCleanup.then(function (c) { return c || cart; });
    b2bCleanup = postJson('/cart/update.js', { attributes: stale })
      .then(function (r) { return r.ok ? r.json() : cart; })
      .catch(function () { return cart; })
      .then(function (c) { b2bCleanup = null; return c; });
    return b2bCleanup;
  }

  /* Login-Links im Drawer/Warenkorb (Club-Hinweis, Willkommenscode): Der Drawer wird über die Section-Rendering-API auf
     „/“ gerendert, der Rücksprung steht serverseitig fest auf dem Warenkorb. Hier zeigt return_to auf die Seite, auf der die
     Kundin gerade ist (nur relative Pfade, wie snippets/kult-login-url.liquid). Auf Konto-/Login-Seiten bleibt der Link. */
  function fixLoginLinks(root) {
    if (!root || /^\/(account|customer_authentication)/.test(window.location.pathname)) return;
    var here = encodeURIComponent(window.location.pathname + window.location.search);
    root.querySelectorAll('a[href^="/customer_authentication/login"]').forEach(function (a) {
      a.setAttribute('href', '/customer_authentication/login?return_to=' + here);
    });
  }

  /* Produktzahl für Badge/Titel (10.10.2026): normale Zeilen mit ihrer Menge, ein Kult-Abo-Set (Zeilen mit _kultabo_set und
     Abo-Plan, in einer Gruppe angezeigt) zählt als 1. Gleiche Rechnung wie snippets/kult-cart-count.liquid. */
  function kultCartCount(cart) {
    var items = (cart && cart.items) || [];
    var sets = {};
    var count = 0;
    items.forEach(function (it) {
      var setId = it.properties && it.properties._kultabo_set;
      if (setId && it.selling_plan_allocation) {
        if (!sets[setId]) { sets[setId] = true; count += 1; }
      } else {
        count += it.quantity || 0;
      }
    });
    return count;
  }
  if (!window.kultCartCount) window.kultCartCount = kultCartCount;

  function broadcast(cart, source) {
    var count = kultCartCount(cart);
    document.querySelectorAll('[data-kult-cart-count]').forEach(function (badge) {
      var changed = badge.textContent !== String(count);
      badge.textContent = count;
      badge.hidden = count === 0;
      if (changed && source !== 'init') { badge.classList.remove('kult-badge-bump'); void badge.offsetWidth; badge.classList.add('kult-badge-bump'); }
    });
    document.querySelectorAll('[data-shipping-bar]').forEach(function (bar) {
      bar.setAttribute('data-cart-total', cart.total_price);
    });
    // Additiv (18.09.2026): Startseite liest 'kult:last-items' für die Nachschub-Leiste.
    // Nur Produktdaten (max. 3 Positionen), keine Personendaten; localStorage kann fehlen → try/catch.
    if (source !== 'init') {
      try {
        window.localStorage.setItem('kult:last-items', JSON.stringify((cart.items || []).slice(0, 3).map(function (i) {
          var um = i.unit_price_measurement; // Grundpreis (03.10.2026, PAngV): Cent je Bezugseinheit + Einheit, z. B. 4360 / „kg“
          return { id: i.variant_id || i.id, handle: i.handle, product_title: i.product_title, variant_title: i.variant_title, price: i.price, unit_price: i.unit_price || 0, unit_ref: um && um.reference_unit ? (um.reference_value && um.reference_value !== 1 ? um.reference_value + ' ' : '') + um.reference_unit : '', quantity: i.quantity, image: i.image, url: i.url };
        })));
      } catch (e) { /* Storage blockiert oder voll: still ignorieren */ }
    }
    document.dispatchEvent(new CustomEvent('kult:cart:updated', { detail: { cart: cart, source: source } }));
  }

  /* ---------- Basis: Zeilen, Cross-Sell, Gutschein, Neu-Rendern ---------- */
  class KultCartBase extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onRemoteUpdate = this._onRemoteUpdate.bind(this);
      this._busy = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.addEventListener('click', this._onClick);
      this.addEventListener('submit', this._onSubmit);
      /* Menge tippen (Okt. 2026): Enter oder Verlassen des Feldes übernimmt die Zahl, 0 entfernt die Zeile */
      this.addEventListener('change', (e) => {
        var input = e.target.closest && e.target.closest('[data-cart-qty-input]');
        if (!input) return;
        var lineEl = input.closest('[data-cart-line]');
        var next = Math.max(0, Math.min(999, parseInt(input.value, 10) || 0));
        if (lineEl) this.changeLine(lineEl, next);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.closest && e.target.closest('[data-cart-qty-input]')) { e.preventDefault(); e.target.blur(); }
      });
      document.addEventListener('kult:cart:updated', this._onRemoteUpdate);
      this.bindDocument();
      fixLoginLinks(this);
      /* Leerer Warenkorb mit hängengebliebenen Firmen-Attributen (z. B. nach /cart/clear.js): einmal je Seitenaufruf bereinigen */
      if (!b2bLoadChecked && (this.getAttribute('data-item-count') || '0') === '0') {
        b2bLoadChecked = true;
        fetchCart().then(function (cart) { return cleanB2bLeftovers(cart, true); }).catch(function () {});
      }
      if (this.welcomeProblem() && (this.getAttribute('data-item-count') || '0') !== '0') {
        var self = this;
        fetchCart().then(function (cart) { self.guardWelcome(cart); }).catch(function () {});
      }
    }

    disconnectedCallback() {
      document.removeEventListener('kult:cart:updated', this._onRemoteUpdate);
      this.unbindDocument();
    }

    bindDocument() {}
    unbindDocument() {}

    /* Section-Rendering-API: ?section_id=… liefert das HTML genau dieser Section
       (funktioniert für statische Sections und Sections aus JSON-Templates). */
    fetchSectionHtml() {
      var id = this.getAttribute('data-section-id');
      var url = this.getAttribute('data-section-url') || '/';
      var tag = this.tagName.toLowerCase();
      return fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(id), { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          return tmp.querySelector(tag);
        });
    }

    notice(text) {
      var el = this.querySelector('[data-cart-notice]');
      if (!el) return;
      el.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12l4 4L19 6"/></svg><span></span>';
      el.querySelector('span').textContent = text;
      el.hidden = false;
      window.clearTimeout(this._noticeTimer);
      this._noticeTimer = window.setTimeout(function () { el.hidden = true; }, 4000);
    }

    refresh(cart, noticeText) {
      var self = this;
      var body = this.querySelector('[data-cart-body]');
      var scrollTop = body ? body.scrollTop : 0;
      /* Firmen-Attribute vor dem Neu-Rendern bereinigen, damit Drawer und Seite sofort den Privat-Stand zeigen */
      return cleanB2bLeftovers(cart).then(function (clean) {
        cart = clean || cart;
        return self.fetchSectionHtml();
      }).then(function (fresh) {
        if (fresh) {
          self.innerHTML = fresh.innerHTML;
          self.setAttribute('data-item-count', fresh.getAttribute('data-item-count') || '0');
          self.setAttribute('data-total', fresh.getAttribute('data-total') || '0');
          fixLoginLinks(self);
          if (fresh.hasAttribute('data-cart-b2b')) self.setAttribute('data-cart-b2b', fresh.getAttribute('data-cart-b2b'));
          ['data-threshold', 'data-club', 'data-club-state'].forEach(function (a) { if (fresh.hasAttribute(a)) self.setAttribute(a, fresh.getAttribute(a)); });
        }
        var newBody = self.querySelector('[data-cart-body]');
        if (newBody) newBody.scrollTop = scrollTop;
        if (noticeText) self.notice(noticeText);
        if (cart) broadcast(cart, self);
        if (cart) self.guardWelcome(cart);
        if (welcomeState.kind && Date.now() - welcomeState.at < 5000) self.showWelcomeError(welcomeState.kind);
      });
    }

    setBusy(state) {
      this._busy = state;
      this.classList.toggle('is-busy', state);
    }

    changeLine(lineEl, quantity) {
      var self = this;
      if (this._busy || !lineEl) return;
      var line = parseInt(lineEl.getAttribute('data-cart-line'), 10);
      var title = lineEl.querySelector('.kult-cart-line__title');
      var name = title ? title.textContent.trim() : 'Artikel';
      this.setBusy(true);
      postJson('/cart/change.js', { line: line, quantity: quantity })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var text = quantity === 0 ? name + ' wurde entfernt.' : 'Menge aktualisiert.';
          return self.refresh(cart, text).then(function () {
            var updated = self.querySelector('[data-cart-line="' + line + '"]');
            if (updated && quantity > 0) {
              updated.classList.add('is-updated');
              window.setTimeout(function () { updated.classList.remove('is-updated'); }, 600);
            }
          });
        })
        .catch(function () { self.notice('Das hat nicht geklappt. Bitte versuche es erneut.'); })
        .then(function () { self.setBusy(false); });
    }

    addVariant(variantId, btn) {
      var self = this;
      if (this._busy) return;
      var label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Wird hinzugefügt…'; }
      var errorEl = this.querySelector('[data-cart-xs-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      this.setBusy(true);
      postJson('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (d) { throw new Error((d && d.description) || 'Der Artikel konnte nicht hinzugefügt werden.'); });
          return r.json();
        })
        .then(function (added) {
          var name = (added.items && added.items[0] && added.items[0].product_title) || added.product_title || 'Artikel';
          return fetchCart().then(function (cart) { return self.refresh(cart, 'Gute Wahl. ' + name + ' liegt im Warenkorb.'); });
        })
        .catch(function (err) {
          if (btn) { btn.disabled = false; btn.textContent = label; }
          var el = self.querySelector('[data-cart-xs-error]');
          if (el) { el.hidden = false; el.textContent = err.message || 'Es ist ein Fehler aufgetreten.'; }
        })
        .then(function () { self.setBusy(false); });
    }

    /* Willkommenscode (10.10.2026): gilt nur mit Kundenkonto und aktivem Newsletter. Liefert '' (erfüllt), 'guest' oder 'news'. */
    isWelcomeCode(code) {
      var list = (this.getAttribute('data-welcome-codes') || '').split(',');
      var wanted = String(code || '').trim().toUpperCase();
      return !!wanted && list.some(function (c) { return c.trim().toUpperCase() === wanted; });
    }

    welcomeProblem() {
      if (this.getAttribute('data-customer-logged-in') !== 'true') return 'guest';
      if (this.getAttribute('data-newsletter-active') !== 'true') return 'news';
      return '';
    }

    showWelcomeError(kind) {
      var el = this.querySelector('[data-cart-coupon-error]');
      if (!el) return;
      var key = kind === 'guest' ? 'guest' : 'news';
      var msg = this.getAttribute('data-welcome-msg-' + key) || '';
      var href = this.getAttribute('data-welcome-link-' + key) || '';
      var label = this.getAttribute('data-welcome-link-' + key + '-label') || '';
      el.textContent = msg;
      if (href && label) {
        var a = document.createElement('a');
        a.className = 'kult-cart-coupon__error-link';
        a.href = href;
        a.textContent = label;
        el.appendChild(a);
        fixLoginLinks(el);
      }
      el.hidden = false;
      var details = this.querySelector('[data-cart-coupon]');
      if (details) details.open = true;
    }

    /* Liegt ein Willkommenscode im Warenkorb (Link /discount/…, älterer Stand) und die Voraussetzung fehlt: entfernen, Hinweis zeigen. */
    guardWelcome(cart) {
      var self = this;
      if (welcomeState.busy || !cart) return;
      var problem = this.welcomeProblem();
      if (!problem) return;
      var codes = (cart.discount_codes || []).map(function (c) { return c.code; }).filter(Boolean);
      var keep = codes.filter(function (c) { return !self.isWelcomeCode(c); });
      if (keep.length === codes.length) return;
      welcomeState.busy = true;
      postJson('/cart/update.js', { discount: keep.join(',') })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var still = (cart.discount_codes || []).some(function (c) { return self.isWelcomeCode(c.code); });
          if (!still) { welcomeState.kind = problem; welcomeState.at = Date.now(); }
          welcomeState.busy = false;
          return self.refresh(cart);
        })
        .catch(function () { welcomeState.busy = false; });
    }

    applyDiscount(code) {
      var self = this;
      var errorEl = this.querySelector('[data-cart-coupon-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (code && this.isWelcomeCode(code)) {
        var problem = this.welcomeProblem();
        if (problem) { this.showWelcomeError(problem); return; }
      }
      this.setBusy(true);
      postJson('/cart/update.js', { discount: code })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var codes = cart.discount_codes || [];
          var applicable = codes.some(function (c) { return c.applicable; });
          if (code && !applicable) {
            return postJson('/cart/update.js', { discount: '' })
              .then(function (r) { return r.json(); })
              .then(function (cleanCart) {
                return self.refresh(cleanCart).then(function () {
                  var details = self.querySelector('[data-cart-coupon]');
                  if (details) details.open = true;
                  var el = self.querySelector('[data-cart-coupon-error]');
                  if (el) { el.hidden = false; el.textContent = 'Dieser Code ist nicht gültig.'; }
                });
              });
          }
          return self.refresh(cart, code ? 'Gutschein angewendet.' : 'Gutschein entfernt.');
        })
        .catch(function () { self.notice('Der Gutschein konnte nicht geprüft werden.'); })
        .then(function () { self.setBusy(false); });
    }

    /* Firmendaten: alle benannten Felder -> cart.attributes (Feldname = Attributname). */
    saveCompany(form) {
      var self = this;
      if (this._busy || !form) return;
      var showError = function (msg) {
        var el = self.querySelector('[data-cart-b2b-error]');
        if (el) { el.hidden = false; el.textContent = msg; }
      };
      var errorEl = this.querySelector('[data-cart-b2b-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      var attributes = {};
      form.querySelectorAll('input[name], select[name], textarea[name]').forEach(function (field) {
        attributes[field.name] = (field.value || '').trim();
      });
      if (!attributes['Firma']) {
        showError('Bitte geben Sie den Firmennamen an.');
        var company = form.querySelector('[name="Firma"]');
        if (company) company.focus();
        return;
      }
      this.setBusy(true);
      postJson('/cart/update.js', { attributes: attributes })
        .then(function (r) {
          if (!r.ok) throw new Error('cart update failed');
          return r.json();
        })
        .then(function (cart) {
          return self.refresh(cart, 'Firmendaten gespeichert.').then(function () {
            var details = self.querySelector('[data-cart-b2b]');
            if (details) details.open = true;
          });
        })
        .catch(function () {
          var details = self.querySelector('[data-cart-b2b]');
          if (details) details.open = true;
          showError('Die Firmendaten konnten nicht gespeichert werden.');
        })
        .then(function () { self.setBusy(false); });
    }

    _onClick(e) {
      // Kult-Abo-Gruppe (snippets/kult-cart-abo-set.liquid): Menge in Gramm-Schritten neu zerlegen bzw. ganze Gruppe entfernen
      var aboStep = e.target.closest('[data-cart-abo-step]');
      if (aboStep) { this.changeAboSet(aboStep.closest('[data-cart-abo-set]'), parseInt(aboStep.getAttribute('data-cart-abo-step'), 10)); return true; }
      var aboRemove = e.target.closest('[data-cart-abo-remove]');
      if (aboRemove) {
        var aboWrap = aboRemove.closest('[data-cart-abo-set]');
        this.updateGroup(aboWrap.getAttribute('data-cart-abo-keys'), 0, (aboWrap.getAttribute('data-cart-abo-name') || 'Kult-Abo') + ' wurde entfernt.');
        return true;
      }
      // Geschenk-Abo-Gruppe (snippets/kult-cart-gift-set.liquid): Beutel + Versand-Position zusammen entfernen
      var giftRemove = e.target.closest('[data-cart-gift-remove]');
      if (giftRemove) {
        var giftWrap = giftRemove.closest('[data-cart-gift-set]');
        this.updateGroup(giftWrap.getAttribute('data-cart-gift-keys'), 0, (giftWrap.getAttribute('data-cart-gift-name') || 'Das Geschenk-Abo') + ' wurde entfernt.');
        return true;
      }
      var step = e.target.closest('[data-cart-step]');
      if (step) {
        var lineEl = step.closest('[data-cart-line]');
        var qtyEl = lineEl && lineEl.querySelector('[data-cart-qty]');
        var current = qtyEl ? parseInt(qtyEl.tagName === 'INPUT' ? qtyEl.value : qtyEl.textContent, 10) || 1 : 1;
        var next = Math.max(0, current + parseInt(step.getAttribute('data-cart-step'), 10));
        this.changeLine(lineEl, next);
        return true;
      }
      var remove = e.target.closest('[data-cart-remove]');
      if (remove) { this.changeLine(remove.closest('[data-cart-line]'), 0); return true; }

      var add = e.target.closest('[data-cart-add]');
      if (add) { this.addVariant(parseInt(add.getAttribute('data-cart-add'), 10), add); return true; }

      var couponRemove = e.target.closest('[data-cart-coupon-remove]');
      if (couponRemove) { this.applyDiscount(''); return true; }

      // Anzahl einer ganzen Konfiguration: alle Zeilen der Gruppe auf die neue Menge
      var gstep = e.target.closest('[data-cart-group-step]');
      if (gstep) {
        var wrap = gstep.closest('[data-cart-group-keys]');
        var cur = parseInt((wrap.querySelector('[data-cart-group-qty]') || {}).textContent, 10) || 1;
        var nextQty = Math.max(1, cur + parseInt(gstep.getAttribute('data-cart-group-step'), 10));
        if (nextQty !== cur) {
          // Versandpositionen: Einzelversand = Anzahl; Sammellieferung = Karton-Staffel (10er, 5er, Einzelpaket), siehe shipCartons
          var per = parseInt(wrap.getAttribute('data-cart-group-ship-per'), 10) || 10;
          var mode = wrap.getAttribute('data-cart-group-ship-mode');
          var midVariant = wrap.getAttribute('data-cart-group-ship-mid');
          var perMid = midVariant ? (parseInt(wrap.getAttribute('data-cart-group-ship-per-mid'), 10) || 5) : 0;
          var need = { single: 0, mid: 0, bulk: 0 };
          // 1 Präsent unter 49 €: keine Versandposition, die Kasse berechnet Firmenversand nach Gewicht (siehe kult-b2b-builder.js)
          var perGross = parseInt(wrap.getAttribute('data-cart-group-per-gross'), 10) || 0;
          var viaCheckout = mode !== 'abholung' && nextQty === 1 && perGross < 4900;
          if (viaCheckout) { /* keine Positionen */ }
          else if (mode === 'einzeln') need.single = nextQty;
          else if (mode === 'firma') need = shipCartons(nextQty, per, perMid);
          var extra = {}, have = {};
          /* Achtung: Shopify-Zeilen-Keys enthalten selbst einen Doppelpunkt (id:hash) — deshalb am LETZTEN Doppelpunkt trennen */
          String(wrap.getAttribute('data-cart-group-ship-map') || '').split(',').forEach(function (pair) { var i = pair.lastIndexOf(':'); if (i < 1) return; var key = pair.slice(0, i), kind = pair.slice(i + 1); have[kind] = true; extra[key] = need[kind] || 0; });
          /* Etikett-Service (15.10.2026): Tüten-Zeile wächst mit der Präsente-Anzahl (Tüten je Präsent × neue Anzahl); Pauschale bleibt 1 */
          String(wrap.getAttribute('data-cart-group-label-map') || '').split(',').forEach(function (pair) { var i = pair.lastIndexOf(':'); if (i < 1) return; var key = pair.slice(0, i), per = parseInt(pair.slice(i + 1), 10); if (per > 0) extra[key] = per * nextQty; });
          var adds = [];
          var variants = { single: wrap.getAttribute('data-cart-group-ship-single'), mid: midVariant, bulk: wrap.getAttribute('data-cart-group-ship-bulk') };
          ['bulk', 'mid', 'single'].forEach(function (kind) {
            if (need[kind] > 0 && !have[kind] && variants[kind]) {
              var props = { _kultb2b: wrap.getAttribute('data-cart-group-id'), _kultb2b_ship_line: mode, _kultb2b_ship_kind: kind, 'Firmenpräsent': wrap.getAttribute('data-cart-group-ship-label') || 'Versand' };
              adds.push({ id: parseInt(variants[kind], 10), quantity: need[kind], properties: props });
            }
          });
          this.updateGroup(wrap.getAttribute('data-cart-group-keys'), nextQty, 'Anzahl der Konfiguration aktualisiert.', extra, adds);
        }
        return true;
      }

      // Konfiguration entfernen: alle Zeilen einer Präsent-Gruppe über ihre Keys (/cart/update.js)
      var group = e.target.closest('[data-cart-remove-group]');
      if (group) { this.removeGroup(group.getAttribute('data-cart-remove-group')); return true; }

      // Warenkorb leeren: erster Klick fragt nach, zweiter Klick leert (/cart/clear.js)
      var clear = e.target.closest('[data-cart-clear]');
      if (clear) { this.clearCart(clear); return true; }
      return false;
    }

    /* Kult-Abo-Menge ändern (Kult Club nach kg, 09.10.2026): neue Menge größter Beutel zuerst zerlegen (wie assets/kult-abo-calc.js),
       neue Beutelzeilen mit neuer _kultabo_set-ID zuerst hinzufügen, dann die alten Zeilen auf 0 – schlägt das Hinzufügen fehl,
       bleibt die bisherige Gruppe unverändert. */
    changeAboSet(wrap, dir) {
      var self = this;
      if (this._busy || !wrap) return;
      var num = function (a, d) { return parseInt(wrap.getAttribute(a), 10) || d; };
      var grams = num('data-cart-abo-grams', 0);
      var next = Math.min(num('data-cart-abo-max', 3000), Math.max(num('data-cart-abo-min', 500), grams + dir * num('data-cart-abo-step-g', 250)));
      if (next === grams) return;
      var variants = {};
      String(wrap.getAttribute('data-cart-abo-variants') || '').split(',').forEach(function (pair) { var p = pair.split(':'); if (p[0] && p[1]) variants[p[0]] = parseInt(p[1], 10); });
      var rest = next, bags = [];
      Object.keys(variants).map(Number).sort(function (a, b) { return b - a; }).forEach(function (size) {
        var n = Math.floor(rest / size);
        if (n > 0) { bags.push({ id: variants[size], quantity: n }); rest -= n * size; }
      });
      if (rest !== 0 || !bags.length) { this.notice('Diese Menge ist gerade nicht lieferbar.'); return; }
      var label = next % 1000 === 0 ? (next / 1000) + ' kg' : next.toLocaleString('de-DE') + ' g';
      var setId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      var plan = num('data-cart-abo-plan', 0);
      var adds = bags.map(function (b, i) {
        var props = { _kultabo_set: setId };
        if (i === 0) props['Abo-Menge'] = label;
        return { id: b.id, quantity: b.quantity, selling_plan: plan, properties: props };
      });
      var updates = {};
      String(wrap.getAttribute('data-cart-abo-keys') || '').split(',').forEach(function (k) { if (k) updates[k] = 0; });
      this.setBusy(true);
      postJson('/cart/add.js', { items: adds })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return postJson('/cart/update.js', { updates: updates }); })
        .then(function (r) { return r.json(); })
        .then(function (cart) { return self.refresh(cart, 'Abo-Menge: ' + label + ' je Lieferung.'); })
        .catch(function () { self.notice('Das hat nicht geklappt. Bitte versuche es erneut.'); })
        .finally(function () { self.setBusy(false); });
    }

    removeGroup(keyList) { this.updateGroup(keyList, 0, 'Die Konfiguration wurde entfernt.'); }

    updateGroup(keyList, quantity, text, extra, adds) {
      var self = this;
      if (this._busy) return;
      var updates = {};
      String(keyList || '').split(',').forEach(function (k) { if (k) updates[k] = quantity; });
      Object.keys(extra || {}).forEach(function (k) { updates[k] = extra[k]; });
      if (!Object.keys(updates).length) return;
      this.setBusy(true);
      postJson('/cart/update.js', { updates: updates })
        .then(function (r) { return r.json(); })
        .then(function (cart) { if (adds && adds.length) return postJson('/cart/add.js', { items: adds }).then(function () { return fetchCart(); }); return cart; })
        .then(function (cart) { return self.refresh(cart, text); })
        .catch(function () { self.notice('Das hat nicht geklappt. Bitte versuche es erneut.'); })
        .finally(function () { self.setBusy(false); });
    }

    clearCart(btn) {
      var self = this;
      if (this._busy) return;
      if (btn.getAttribute('data-cart-clear') !== 'confirm') {
        btn.setAttribute('data-cart-clear', 'confirm');
        btn.textContent = btn.getAttribute('data-confirm-label') || 'Wirklich alles entfernen?';
        btn.classList.add('is-confirm');
        window.clearTimeout(this._clearTimer);
        this._clearTimer = window.setTimeout(function () { btn.setAttribute('data-cart-clear', ''); btn.textContent = btn.getAttribute('data-label') || 'Warenkorb leeren'; btn.classList.remove('is-confirm'); }, 5000);
        return;
      }
      window.clearTimeout(this._clearTimer);
      this.setBusy(true);
      postJson('/cart/clear.js', {})
        .then(function (r) { return r.json(); })
        .then(function (cart) { return self.refresh(cart, 'Warenkorb geleert.'); })
        .catch(function () { self.notice('Das hat nicht geklappt. Bitte versuche es erneut.'); })
        .finally(function () { self.setBusy(false); });
    }

    _onSubmit(e) {
      var b2bForm = e.target.closest('[data-cart-b2b-form]');
      if (b2bForm) { e.preventDefault(); this.saveCompany(b2bForm); return; }
      var form = e.target.closest('[data-cart-coupon-form]');
      if (!form) return;
      e.preventDefault();
      var input = form.querySelector('[data-cart-coupon-input]');
      var code = input ? input.value.trim() : '';
      if (!code) return;
      this.applyDiscount(code);
    }

    /* Änderung kam vom jeweils anderen Element (Drawer <-> Seite): still neu rendern. */
    _onRemoteUpdate(e) {
      if (!e.detail || e.detail.source === this) return;
      this.refresh(null);
    }
  }

  /* ---------- Drawer: Öffnen/Schließen, Fokusfalle, Add-Events ---------- */
  class KultCartDrawer extends KultCartBase {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onExternalOpen = this._onExternalOpen.bind(this);
      this._onAdd = this._onAdd.bind(this);
      this._onHorizonAdd = this._onHorizonAdd.bind(this);
      this._trigger = null;
    }

    bindDocument() {
      document.addEventListener('click', this._onExternalOpen);
      document.addEventListener('kult:cart:add', this._onAdd);
      document.addEventListener('shopify:cart:lines-update', this._onHorizonAdd);
      document.addEventListener('keydown', this._onKeydown);
    }

    unbindDocument() {
      document.removeEventListener('click', this._onExternalOpen);
      document.removeEventListener('kult:cart:add', this._onAdd);
      document.removeEventListener('shopify:cart:lines-update', this._onHorizonAdd);
      document.removeEventListener('keydown', this._onKeydown);
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open(trigger) {
      if (trigger) this._trigger = trigger;
      this.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      var panel = this.querySelector('[data-cart-panel]');
      var first = panel && panel.querySelector('[data-cart-close]');
      window.setTimeout(function () { (first || panel).focus(); }, 50);
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (this._trigger && typeof this._trigger.focus === 'function') this._trigger.focus();
      this._trigger = null;
    }

    _onClick(e) {
      var closeBtn = e.target.closest('[data-cart-close]');
      if (closeBtn) { e.preventDefault(); this.close(); return true; }
      return super._onClick(e);
    }

    // Kein Drawer nur im Firmenkundenbereich (Header data-kult-mode="b2b": B2B-Templates und Warenkorbseite mit Firmenbestellung).
    // Im Privatkundenbereich bleibt der Drawer aktiv, auch wenn eine Firmenbestellung im Warenkorb liegt.
    isB2b() { var h = document.querySelector('[data-kult-mode]'); return !!h && h.getAttribute('data-kult-mode') === 'b2b'; }

    _onExternalOpen(e) {
      var opener = e.target.closest('[data-kult-cart-open]');
      if (!opener) return;
      if (this.isB2b()) return; // Link führt regulär auf die Warenkorbseite
      e.preventDefault();
      this.open(opener);
    }

    /* Horizon-Standardformulare (z. B. Gutschein auf templates/product.json) melden ein Hinzufügen über das Shopify-Standard-Event.
       Brücke auf kult:cart:add, damit Drawer, Zähler und Ankündigungsleiste reagieren. */
    _onHorizonAdd(e) {
      var action = e && (e.action || (e.detail && e.detail.action));
      if (action !== 'add') return;
      var wait = (e.promise || (e.detail && e.detail.promise) || Promise.resolve());
      Promise.resolve(wait).catch(function () { return null; }).then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      }).then(function (r) { return r.json(); }).then(function (cart) {
        document.dispatchEvent(new CustomEvent('kult:cart:add', { detail: { cart: cart } }));
      }).catch(function () { /* Seite zeigt den Warenkorb beim nächsten Laden */ });
    }

    _onAdd(e) {
      var self = this;
      var cart = e.detail && e.detail.cart;
      var name = e.detail && e.detail.title;
      var text = name ? 'Gute Wahl. ' + name + ' liegt im Warenkorb.' : 'Gute Wahl. Der Artikel liegt im Warenkorb.';
      var done = function (c) {
        var hasB2b = !!(c && c.items && c.items.some(function (it) { return it.properties && it.properties._kultb2b; }));
        if (self.isB2b()) {
          /* Firmenkunden-Bereich: erst das Attribut „Firmenbestellung“ setzen, dann auf /cart?view=b2b (Cowork Mo-23 U1, 12.10.2026).
             (assets/kult-b2b-shop.js, Firmenkunden-Shop). Die Warenkorbseite erkennt die Firmenbestellung zusätzlich serverseitig an
             _kultb2b_quelle; spätestens nach 2,5 s geht es weiter. */
          var cartUrl = self.getAttribute('data-cart-url') || '/cart';
          var go = function () { window.location.href = cartUrl + (cartUrl.indexOf('?') > -1 ? '&' : '?') + 'view=b2b'; };
          var attrs = (c && c.attributes) || {};
          if (attrs.Firmenbestellung || !(window.KultB2bShop && window.KultB2bShop.mark)) { go(); return; } // Konfigurator/Bürokaffee setzen ihre Attribute selbst
          var mark = window.KultB2bShop.mark();
          var left = false;
          var leave = function () { if (left) return; left = true; go(); };
          Promise.resolve(mark).catch(function () { return null; }).then(leave);
          window.setTimeout(leave, 2500);
          return;
        }
        if (!hasB2b) { try { window.localStorage.removeItem('kult:b2b'); } catch (err) { /* kein Speicher */ } }
        return self.refresh(c, text).then(function () { self.open(document.activeElement); });
      };
      if (cart) done(cart); else fetchCart().then(done);
    }

    _onKeydown(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      var panel = this.querySelector('[data-cart-panel]');
      if (!panel) return;
      var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ---------- Warenkorb-Seite: nur Basis-Verhalten ---------- */
  // Warenkorbseite: Leerzustand als Firmenbestellung, wenn zuletzt eine Firmenbestellung im Warenkorb lag (localStorage kult:b2b)
  class KultCartPage extends KultCartBase {
    connectedCallback() { super.connectedCallback(); this.applyEmptyMode(); this.setupSticky(); }
    disconnectedCallback() { if (super.disconnectedCallback) super.disconnectedCallback(); if (this._stickyIo) { this._stickyIo.disconnect(); this._stickyIo = null; } }
    /* Mobile Kassenleiste (snippets/kult-cart-sticky, Cowork Mo-23 U6, 12.10.2026): sichtbar, solange der Kassen-Button der
       Zusammenfassung nicht im Bild ist; Betrag und Zusatz aus der Gesamt-Zeile. Ab 960 px blendet CSS die Leiste aus. */
    setupSticky() {
      if (this._stickyIo) { this._stickyIo.disconnect(); this._stickyIo = null; }
      var bar = this.querySelector('[data-cart-sticky]');
      if (!bar) { document.body.removeAttribute('data-kult-cart-sticky'); return; }
      var btn = this.querySelector('.kult-cart-page__summary .kult-cart-checkout__btn');
      if (!btn || !document.getElementById('kult-cart-form-page') || !('IntersectionObserver' in window)) { bar.hidden = true; return; }
      var total = this.querySelector('.kult-cart-page__summary [data-cart-total-label]');
      var note = this.querySelector('.kult-cart-page__summary .kult-cart-totals__row--total small');
      var t = bar.querySelector('[data-cart-sticky-total]');
      var n = bar.querySelector('[data-cart-sticky-note]');
      if (t) t.textContent = total ? total.textContent.trim() : '';
      if (n) n.textContent = note ? note.textContent.trim() : '';
      this._stickyIo = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        bar.hidden = e.isIntersecting;
        if (e.isIntersecting) document.body.removeAttribute('data-kult-cart-sticky'); else document.body.setAttribute('data-kult-cart-sticky', '');
      }, { threshold: 0 });
      this._stickyIo.observe(btn);
    }
    applyEmptyMode() {
      var flag = false;
      try {
        if (this.getAttribute('data-cart-b2b') === 'true') window.localStorage.setItem('kult:b2b', '1');
        /* Privat-Warenkorb mit Artikeln: altes Firmen-Merkmal löschen (Cowork Z3, 12.10.2026) */
        else if ((this.getAttribute('data-item-count') || '0') !== '0') window.localStorage.removeItem('kult:b2b');
        flag = window.localStorage.getItem('kult:b2b') === '1';
      } catch (err) { flag = false; }
      /* Aufruf aus dem Firmenkunden-Bereich (/cart?view=b2b): leerer Warenkorb immer als Firmenbestellung */
      if (/[?&]view=b2b(&|$)/.test(window.location.search)) flag = true;
      var b2b = flag && (this.getAttribute('data-item-count') || '0') === '0';
      var toggle = function (sel, show) { var el = document.querySelector(sel); if (el) el.hidden = !show; };
      toggle('[data-cart-empty-b2b]', b2b);
      toggle('.kult-cart__empty:not([data-cart-empty-b2b])', !b2b);
      toggle('[data-cart-title-b2b]', b2b);
      toggle('[data-cart-title-b2c]', !b2b);
      toggle('[data-cart-continue-b2c]', !b2b);
      if (b2b) document.body.setAttribute('data-kult-cart-b2b', ''); else document.body.removeAttribute('data-kult-cart-b2b');
    }
    refresh(cart, text) { var self = this; return super.refresh(cart, text).then(function () { self.applyEmptyMode(); self.setupSticky(); }); }
  }

  if (!customElements.get('kult-cart-drawer')) customElements.define('kult-cart-drawer', KultCartDrawer);
  if (!customElements.get('kult-cart-page')) customElements.define('kult-cart-page', KultCartPage);

  window.KultCart = { formatPrice: formatPrice, fetchCart: fetchCart, hasB2bLine: hasB2bLine };
})();
